import React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  DEFAULT_REPORT_FILTERS,
  RESULT_OPTIONS,
  SORT_OPTIONS,
  STATUS_OPTIONS,
  hasActiveFilters,
} from '../../utils/reportFilterUtils';
import { BRAND } from '../../theme/brandTheme';

const chipSx = { fontFamily: BRAND.fontFamily };

/** Dashboard report filters — syncs state via parent + URL */
const ReportFilterBar = ({ filters, onChange, totalCount, visibleCount }) => {
  const active = hasActiveFilters(filters);

  const toggleArrayValue = (key, value) => {
    const current = filters[key] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  };

  const handleClear = () => onChange({ ...DEFAULT_REPORT_FILTERS });

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${BRAND.borderLight}`,
        bgcolor: '#fafafa',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterListIcon sx={{ color: BRAND.purple }} />
          <Typography sx={{ fontWeight: 600, color: BRAND.purple, fontFamily: BRAND.fontFamily }}>
            Filtreler
          </Typography>
          {totalCount > 0 && (
            <Chip
              size="small"
              label={`${totalCount} testten ${visibleCount}'i gösteriliyor`}
              sx={{
                ...chipSx,
                bgcolor: active ? BRAND.purple : '#e8e8e8',
                color: active ? '#fff' : BRAND.textGray,
              }}
            />
          )}
        </Stack>
        {active && (
          <Button size="small" onClick={handleClear} sx={{ textTransform: 'none' }}>
            Temizle
          </Button>
        )}
      </Stack>

      <Stack spacing={2}>
        {/* Status multi-select */}
        <FormControl size="small" fullWidth>
          <InputLabel>Durum</InputLabel>
          <Select
            multiple
            value={filters.status}
            onChange={(e) => onChange({ ...filters, status: e.target.value })}
            input={<OutlinedInput label="Durum" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((v) => (
                  <Chip key={v} label={v} size="small" sx={chipSx} />
                ))}
              </Box>
            )}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {/* Result multi-select */}
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Sonuç</InputLabel>
            <Select
              multiple
              value={filters.result}
              onChange={(e) => onChange({ ...filters, result: e.target.value })}
              input={<OutlinedInput label="Sonuç" />}
              renderValue={(selected) => selected.join(', ')}
            >
              {RESULT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Significance */}
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Anlamlılık</InputLabel>
            <Select
              value={filters.significant}
              label="Anlamlılık"
              onChange={(e) => onChange({ ...filters, significant: e.target.value })}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="yes">Significant</MenuItem>
              <MenuItem value="no">Not significant</MenuItem>
            </Select>
          </FormControl>

          {/* Sort */}
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Sıralama</InputLabel>
            <Select
              value={filters.sort}
              label="Sıralama"
              onChange={(e) => onChange({ ...filters, sort: e.target.value })}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Date range */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Başlangıç"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            label="Bitiş"
            type="date"
            value={filters.dateTo}
            onChange={(e) => onChange({ ...filters, dateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>

        {/* Quick chips — mobile friendly toggles */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexWrap: 'wrap', gap: 1 }}>
          {STATUS_OPTIONS.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              size="small"
              onClick={() => toggleArrayValue('status', opt)}
              color={filters.status.includes(opt) ? 'primary' : 'default'}
              sx={chipSx}
            />
          ))}
        </Box>
      </Stack>
    </Box>
  );
};

export default ReportFilterBar;
